import React from 'react';

import { ReactComponent as CloseIconComponent } from 'svg/close.svg';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { useKeyDown } from 'hooks/useKeyDown';
import {
  createSidebarModalBlockClassName,
  createSidebarModalSectionClassName,
  createSidebarModalSplitClassName,
  createSidebarModalWindowClassName,
} from './sidebarModalStyles';

type Size = 'm' | 'l';

type WindowProps = {
  size?: Size;
  onMisclick?(): void;
  onEscape?(): void;
  onClose?(): void;
};

export const SidebarModalWindowComponent: React.FC<WindowProps> = (props) => {
  const { children, size = 'm', onMisclick, onEscape, onClose } = props;

  const handleClickOutside = React.useCallback(() => {
    if (onMisclick) {
      onMisclick();
    }
  }, [onMisclick]);

  const windowElementRef = useOutsideClick<HTMLDivElement>(handleClickOutside);

  const handleEscapePress = React.useCallback(() => {
    if (onEscape) {
      onEscape();
    }
  }, [onEscape]);

  useKeyDown({
    Escape: handleEscapePress,
  });

  const wrapperClassName = createSidebarModalWindowClassName('wrapper', {
    size,
  });

  const closeButtonClassName = createSidebarModalWindowClassName(
    'closeButton',
    {
      size,
    }
  );

  const contentClassName = createSidebarModalWindowClassName('content', {
    size,
  });

  return (
    <div ref={windowElementRef} className={wrapperClassName}>
      {!!onClose && (
        <button className={closeButtonClassName} onClick={onClose}>
          <CloseIconComponent />
        </button>
      )}
      <div className={contentClassName}>{children}</div>
    </div>
  );
};

type SectionProps = {
  content: React.ReactNode;
  actions: React.ReactNode;
  theme?: 'head' | 'body';
  navigation?: React.ReactNode;
  lastSectionHasBorder?: boolean;
};

export const SidebarModalSectionComponent: React.FC<SectionProps> = (props) => {
  const {
    content,
    actions,
    theme = 'body',
    navigation,
    lastSectionHasBorder = false,
  } = props;

  const wrapperClassName = createSidebarModalSectionClassName('wrapper', {
    theme,
    lastSectionHasBorder,
  });

  const contentClassName = createSidebarModalSectionClassName('content', {
    theme,
  });

  const actionsClassName = createSidebarModalSectionClassName('actions', {
    theme,
  });

  const navigationClassName = createSidebarModalSectionClassName('navigation', {
    theme,
  });

  return (
    <div className={wrapperClassName}>
      <div className={contentClassName}>{content}</div>
      <div className={actionsClassName}>{actions}</div>
      <div className={navigationClassName}>{navigation}</div>
    </div>
  );
};

type BlockProps = {
  withBorder?: boolean;
  withoutBottomPadding?: boolean;
};

export const SidebarModalBlockComponent: React.FC<BlockProps> = (props) => {
  const { children, withBorder, withoutBottomPadding } = props;

  const wrapperClassName = createSidebarModalBlockClassName('wrapper', {
    withBorder,
    withoutBottomPadding,
  });

  const slotClassName = createSidebarModalBlockClassName('slot', {
    withBorder,
    withoutBottomPadding,
  });

  return (
    <div className={wrapperClassName}>
      <div className={slotClassName}>{children}</div>
    </div>
  );
};

type SplitProps = {
  left: React.ReactNode;
  right: React.ReactNode;
};

export const SidebarModalSplitComponent: React.FC<SplitProps> = (props) => {
  const { left, right } = props;

  const wrapperClassName = createSidebarModalSplitClassName('wrapper');

  const leftSlotClassName = createSidebarModalSplitClassName('leftSlot');

  const rightSlotClassName = createSidebarModalSplitClassName('rightSlot');

  return (
    <div className={wrapperClassName}>
      <div className={leftSlotClassName}>{left}</div>
      <div className={rightSlotClassName}>{right}</div>
    </div>
  );
};
